"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Input } from "@heroui/react";
import { Search } from "lucide-react";

interface SearchInputProps {
  initialValue?: string;
}

function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default function SearchInput({ initialValue = "" }: SearchInputProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const previousSearchTerm = useRef(initialValue);

  useEffect(() => {
    // Only update URL if the search term actually changed
    if (debouncedSearchTerm !== previousSearchTerm.current) {
      const params = new URLSearchParams(searchParams);

      if (debouncedSearchTerm) {
        params.set("search", debouncedSearchTerm);
      } else {
        params.delete("search");
      }

      // Only reset to page 1 when search term changes
      params.delete("page");

      // Preserve the current filter panel state when searching
      const currentFiltersParam = searchParams.get("filters");
      if (currentFiltersParam === "on") {
        params.set("filters", "on");
      } else {
        params.delete("filters"); // Don't force filters open
      }

      // Get current pathname to maintain URL structure
      const currentPath = window.location.pathname;
      const queryString = params.toString();
      const newUrl = `${currentPath}?${queryString}`;

      // Use replace instead of push for search to avoid cluttering browser history
      router.replace(newUrl, { scroll: false });
      previousSearchTerm.current = debouncedSearchTerm;
    }
  }, [debouncedSearchTerm, router, searchParams]);

  return (
    <Input
      placeholder="Search events..."
      className="rounded-md"
      value={searchTerm}
      variant="faded"
      onChange={(e) => setSearchTerm(e.target.value)}
      startContent={<Search className="w-4 h-4" />}
      size="md"
      classNames={{
        inputWrapper: "bg-surface/50 rounded-md",
      }}
    />
  );
}
